/** @jsxImportSource @emotion/react */
import Root from "pages/root";
import {
  AddonBody,
  AddonContainer,
  AddonHeader,
  AddonItem,
  Container,
  ProfileContainer,
  ProfileImg,
  ProfileName,
  Rectangle,
  TitleContainer,
} from "./index.styles";
import UI from "@mile-meeting/mile-ui";
import { css } from "@emotion/react";
import { useToast } from "hook/useToast";
import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { getMyMemberinfo } from "apis/members/my";
import { getPersonalCalendarConnection } from "apis/personal-calendar";
import {
  getDetectionPernalCalendarSetting,
  linkDetectionPersonalCalendar,
  unlinkDetectionPersonalCalendar,
} from "apis/visitors/personal-calendar";
import { getMessengers } from "apis/messangers";
import { SyncLoader } from "react-spinners";
import { is } from "date-fns/locale";

const MyInfo = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();

  const [loading, setLoading] = useState<boolean>(false);
  const [memberName, setMemberName] = useState<string>("-");
  const [memberImage, setMemberImage] = useState<string | null>();
  const [isLinkedGoogleCalendar, setIsLinkedGoogleCalendar] = useState<boolean>(false);
  const [isLinkedSlack, setIsLinkedSlack] = useState<boolean>(false);
  const [isCalendarDetected, setIsCalendarDetected] = useState<boolean>(false);

  const navigateEdit = () => {
    navigate(`/setting/${encodeURIComponent("내 정보 관리")}/edit`, {
      state: {
        memberName: memberName,
        memberImage: memberImage,
      },
    });
  };

  const onChangeDetectGoogleCalendar = async (value: boolean) => {
    if (!isLinkedSlack) {
      showToast("메신저 연동이 필요한 기능입니다.", "warning");
      return;
    }
    setLoading(true);

    let res;
    if (value) res = await linkDetectionPersonalCalendar();
    else res = await unlinkDetectionPersonalCalendar();

    if (res.success) {
      setIsCalendarDetected(value);
      if (value) showToast("외부 인원 일정 감지 기능이 활성화되었습니다.", "success");
      else showToast("외부 인원 일정 감지 기능이 비활성화되었습니다.", "success");
    } else {
      showToast(res.message ?? "오류가 발생했습니다. 다시 시도해 주세요.", "error");
    }

    setLoading(false);
  };

  const getMyInfo = async () => {
    const res = await getMyMemberinfo();
    if (res.success) {
      setMemberName(res.data?.nickname ?? "");
      setMemberImage(res.data?.image ?? "");
    }
  };

  useEffect(() => {
    getMyInfo();

    getPersonalCalendarConnection().then((res) => {
      setIsLinkedGoogleCalendar(res.success);
    });
    getMessengers().then((res) => {
      if (res.data?.find((item) => item.messengerProvider === "SLACK")) {
        setIsLinkedSlack(true);
      }
    });
    getDetectionPernalCalendarSetting().then((res) => {
      if (res.success) {
        setIsCalendarDetected(res.data ?? false);
      }
    });
  }, []);

  useEffect(() => {
    if (location.state?.updateSuccess) {
      showToast("내 정보를 수정했습니다.", "success");
      window.history.replaceState({}, "");
    }
  }, [location]);

  return (
    <Container>
      {loading && (
        <SyncLoader
          color={`var(--alias-color-label-alternative)`}
          loading={loading}
          size={15}
          style={{
            position: "fixed",
            top: "50%",
            left: "50%",
          }}
        />
      )}
      <TitleContainer>
        <UI.Typography
          type="title03"
          fontStyle="bold"
          color="var(--alias-color-label-netural)"
          style={{ padding: "24px 20px" }}
        >
          내 정보 관리
        </UI.Typography>
        <div style={{ padding: "8px 20px" }}>
          <UI.Button title="내 정보 수정하기" onClick={navigateEdit} />
        </div>
      </TitleContainer>
      <Rectangle />
      <ProfileContainer>
        <ProfileImg
          src={
            memberImage ? memberImage : "https://mile-storage.s3.ap-northeast-2.amazonaws.com/web/mile-logo-gray.png"
          }
        />
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 4px;
            text-align: center;
          `}
        >
          <UI.Typography type="label01" fontStyle="medium" color="var(--alias-color-label-alternative">
            이름
          </UI.Typography>
          <ProfileName>
            <UI.Typography type="headline01" fontStyle="bold">
              {memberName}
            </UI.Typography>
          </ProfileName>
        </div>
      </ProfileContainer>
      {isLinkedGoogleCalendar && (
        <AddonContainer>
          <AddonHeader>
            <UI.Typography type="heading02" fontStyle="bold" style={{ padding: "20px" }}>
              외부 인원 일정 감지 및 자동 초대장 전송
            </UI.Typography>
          </AddonHeader>
          <AddonBody>
            <AddonItem>
              <div style={{ display: "flex", flexDirection: "column", gap: "4px", justifyContent: "center" }}>
                <UI.Typography type="body02 normal" fontStyle="regular" color="var(--alias-color-label-normal">
                  연동된 캘린더에서 <strong>한 달 내</strong> 의 외부 인원과의 일정을 감지하여, 방문 초대장 전송 여부를
                  메신저로 받아보기
                </UI.Typography>
                {!isLinkedSlack && (
                  <UI.Typography type="label01" fontStyle="medium" color="var(--alias-color-status-negative)">
                    - 메신저 연동이 필요한 기능입니다. 메신저 연동을 진행해 주세요.
                  </UI.Typography>
                )}
              </div>
              <div onClick={() => onChangeDetectGoogleCalendar(!isCalendarDetected)}>
                <UI.Selection.Toggle.Default state={isCalendarDetected} disabled={!isLinkedSlack} size="large" />
              </div>
            </AddonItem>
          </AddonBody>
        </AddonContainer>
      )}
    </Container>
  );
};

export default MyInfo;
